import * as React from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useForm } from "~/config/react-hook-form";
import { Column, Columns } from "~/design-system/Columns";
import { DrawerClose } from "~/design-system/Drawer";
import { Heading } from "~/design-system/Heading";
import { Stack } from "~/design-system/Stack";
import { CloseButton } from "~/screens/App/components/CloseButton";
import { createComponentHook } from "~/types";
import type { Filters } from "../../../../../../UserNotificationsScreen";
import { CategoriesFilter } from "./CategoriesFilter";
import { ProjectFilter } from "./ProjectFilter";
import { TypesFilter } from "./TypesFilter";

type NotificationsFilterContentProps = {
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  filters: Filters;
  inDialog?: boolean;
};

const Header = (props: {
  inDialog?: boolean;
  children: React.ReactElement;
}) => {
  if (!props.inDialog) {
    return props.children;
  }
  return (
    <Columns alignY="center">
      <Column>{props.children}</Column>
      <Column width="content">
        <DrawerClose asChild>
          <CloseButton dataClovisActionId="user-notifications-filter-close" />
        </DrawerClose>
      </Column>
    </Columns>
  );
};

const useNotificationsFilterContent = createComponentHook(
  (props: NotificationsFilterContentProps) => {
    const { t } = useTranslation();
    const validationSchema = z.object({
      projectId: z.array(z.string().uuid()).optional(),
    });
    const form = useForm(validationSchema);

    React.useEffect(() => {
      props.setFilters({
        ...props.filters,
        projectIds: form.getValues().projectId ?? [],
      });
    }, [form.watch().projectId]);

    return {
      actions: {
        registerInput: form.register,
      },
      state: {
        control: form.control,
      },
      t,
    };
  }
);

const NotificationsFilterContent = (props: NotificationsFilterContentProps) => {
  const { actions, state, t } = useNotificationsFilterContent(props);

  return (
    <Stack space="gutter">
      <Header inDialog={props.inDialog}>
        <Heading level="3" css={{ color: "$gray700" }}>
          {t(
            "screens.UserNotificationsScreen.NotificationsFilterContent.filterNotifications",
            "Filter notifications"
          )}
        </Heading>
      </Header>
      <TypesFilter setFilters={props.setFilters} filters={props.filters} />
      <ProjectFilter
        {...actions.registerInput("projectId")}
        control={state.control}
        dataClovisActionId="notifications-filter"
      />
      <CategoriesFilter setFilters={props.setFilters} filters={props.filters} />
    </Stack>
  );
};

export type { NotificationsFilterContentProps };
export { NotificationsFilterContent };
